import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "./analytics";

export const LEVELS = [
  { level: 1, name: "Budget Newbie", xpRequired: 0 },
  { level: 2, name: "Expense Explorer", xpRequired: 1000 },
  { level: 3, name: "Mindful Spender", xpRequired: 2000 },
  { level: 4, name: "Budget Sage", xpRequired: 3000 },
  { level: 5, name: "Zen Accountant", xpRequired: 4000 },
];

export const XP_REWARDS = {
  LOG_EXPENSE: 10,
  WEEKLY_REFLECTION: 50,
  MONTH_COMPLETE: 100,
};

export type StreakState = 'active' | 'safe' | 'grace' | 'frozen' | 'reset';

export interface UserProgress {
  user_id: string;
  xp: number;
  level: number;
  current_streak: number;
  longest_streak: number;
  last_activity_date: string | null;
  total_expenses_logged: number;
  total_reflections_completed: number;
  months_completed: number;
  last_grace_date: string | null;
  grace_used_count: number;
  // New fields
  streak_state: StreakState;
  streaks_broken_count: number;
  coffee_count: number;
  max_merchant_count: number;
  recurring_count: number;
  months_under_budget: string[];
}

export const calculateLevel = (xp: number) => {
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (xp >= LEVELS[i].xpRequired) {
      const nextLevel = LEVELS[i + 1];
      return {
        ...LEVELS[i],
        xpToNextLevel: nextLevel ? nextLevel.xpRequired - xp : 0,
        nextLevelXp: nextLevel?.xpRequired || LEVELS[i].xpRequired,
      };
    }
  }
  return {
    ...LEVELS[0],
    xpToNextLevel: LEVELS[1].xpRequired,
    nextLevelXp: LEVELS[1].xpRequired,
  };
};

export const ensureUserProgress = async (userId: string): Promise<UserProgress> => {
  const { data, error } = await supabase
    .from('user_progress')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) { console.error('Error fetching user progress:', error); throw error; }

  if (!data) {
    const { data: newProgress, error: insertError } = await supabase
      .from('user_progress')
      .insert({ user_id: userId })
      .select()
      .single();
    if (insertError) { console.error('Error creating user progress:', insertError); throw insertError; }
    return {
      ...newProgress,
      streak_state: (newProgress.streak_state as StreakState) ?? 'active',
      streaks_broken_count: newProgress.streaks_broken_count ?? 0,
      coffee_count: newProgress.coffee_count ?? 0,
      max_merchant_count: newProgress.max_merchant_count ?? 0,
      recurring_count: newProgress.recurring_count ?? 0,
      months_under_budget: (newProgress.months_under_budget as string[]) ?? [],
    };
  }

  return {
    ...data,
    streak_state: (data.streak_state as StreakState) ?? 'active',
    streaks_broken_count: data.streaks_broken_count ?? 0,
    coffee_count: data.coffee_count ?? 0,
    max_merchant_count: data.max_merchant_count ?? 0,
    recurring_count: data.recurring_count ?? 0,
    months_under_budget: (data.months_under_budget as string[]) ?? [],
  };
};

export const awardXP = async (
  userId: string,
  amount: number,
  source: string = 'expense'
): Promise<{ levelUp: boolean; newLevel?: number; oldLevel?: number }> => {
  const progress = await ensureUserProgress(userId);
  const oldLevel = calculateLevel(progress.xp).level;
  const newXP = progress.xp + amount;
  const newLevel = calculateLevel(newXP).level;
  const levelUp = newLevel > oldLevel;

  const updates: Record<string, unknown> = {
    xp: newXP,
    level: newLevel,
  };

  if (source === 'expense') {
    updates.total_expenses_logged = progress.total_expenses_logged + 1;
  } else if (source === 'reflection') {
    updates.total_reflections_completed = progress.total_reflections_completed + 1;
  } else if (source === 'month') {
    updates.months_completed = progress.months_completed + 1;
  }

  const { error } = await supabase
    .from('user_progress')
    .update(updates)
    .eq('user_id', userId);

  if (error) { console.error('Error awarding XP:', error); throw error; }

  trackEvent({ name: 'xp_gained', props: { amount, source } });
  if (levelUp) { trackEvent({ name: 'level_up', props: { new_level: newLevel } }); }

  return { levelUp, newLevel, oldLevel };
};

export interface StreakUpdateResult {
  newStreak: number;
  graceUsed: boolean;
  streakState: StreakState;
  daysSinceLast: number;
  previousLongest: number;
  streakBroken: boolean;
}

/**
 * New 5-state streak logic.
 * States: active, safe, grace, frozen, reset
 * No noon cutoff — grace applies automatically for 2-day gaps.
 */
export const updateStreak = async (userId: string): Promise<StreakUpdateResult> => {
  const progress = await ensureUserProgress(userId);
  const today = new Date().toISOString().split('T')[0];
  const lastActivity = progress.last_activity_date;
  const previousLongest = progress.longest_streak;

  let newStreak = progress.current_streak;
  let graceUsed = false;
  let streakState: StreakState = 'active';
  let daysSinceLast = 0;
  let streakBroken = false;

  if (!lastActivity) {
    // First ever activity
    newStreak = 1;
    streakState = 'active';
  } else {
    const lastDate = new Date(lastActivity);
    const todayDate = new Date(today);
    const diffTime = todayDate.getTime() - lastDate.getTime();
    daysSinceLast = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (daysSinceLast === 0) {
      // Same day — no change
      return {
        newStreak: progress.current_streak,
        graceUsed: false,
        streakState: progress.streak_state ?? 'active',
        daysSinceLast: 0,
        previousLongest,
        streakBroken: false,
      };
    } else if (daysSinceLast === 1) {
      // Consecutive day
      newStreak = progress.current_streak + 1;
      streakState = 'active';
    } else if (daysSinceLast === 2) {
      // Grace: 2-day gap — auto grace, no noon cutoff
      newStreak = progress.current_streak + 1;
      graceUsed = true;
      streakState = 'active';
    } else if (daysSinceLast >= 3 && daysSinceLast <= 6) {
      // Frozen → reset on log
      newStreak = 1;
      streakState = 'active';
      streakBroken = true;
    } else {
      // 7+ day gap — full reset, triggers still_here
      newStreak = 1;
      streakState = 'active';
      streakBroken = true;
    }
  }

  const longestStreak = Math.max(previousLongest, newStreak);

  const updates: Record<string, unknown> = {
    current_streak: newStreak,
    longest_streak: longestStreak,
    last_activity_date: today,
    streak_state: streakState,
  };

  if (graceUsed) {
    updates.grace_used_count = (progress.grace_used_count ?? 0) + 1;
    updates.last_grace_date = today;
    trackEvent({ name: 'grace_window_used', props: { streak_days: newStreak } });
  }

  if (streakBroken) {
    updates.streaks_broken_count = (progress.streaks_broken_count ?? 0) + 1;
  }

  const { error } = await supabase
    .from('user_progress')
    .update(updates)
    .eq('user_id', userId);

  if (error) { console.error('Error updating streak:', error); throw error; }

  if (!graceUsed) {
    trackEvent({ name: 'streak_updated', props: { streak_days: newStreak } });
  }

  return { newStreak, graceUsed, streakState, daysSinceLast, previousLongest, streakBroken };
};

export const getUserProgress = async (userId: string): Promise<UserProgress> => {
  return ensureUserProgress(userId);
};

/**
 * Compute streak state for DISPLAY (not on log — this is for reading current state).
 * Called when rendering streak UI.
 */
export const getStreakDisplayState = (progress: UserProgress): StreakState => {
  if (!progress.last_activity_date || progress.current_streak === 0) {
    return 'reset';
  }

  const lastDate = new Date(progress.last_activity_date);
  const today = new Date();
  const diffTime = today.getTime() - lastDate.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'active';
  if (diffDays === 1) return 'safe';
  if (diffDays === 2) return 'grace';
  if (diffDays >= 3 && diffDays <= 6) return 'frozen';
  return 'reset'; // 7+
};

/** @deprecated Use getStreakDisplayState instead */
export const getStreakStatus = (progress: UserProgress): 'active' | 'paused' | 'new' => {
  const state = getStreakDisplayState(progress);
  if (state === 'active' || state === 'safe') return 'active';
  if (state === 'grace' || state === 'frozen') return 'paused';
  return 'new';
};

/** @deprecated */
export const getGraceStatus = (progress: UserProgress): {
  available: boolean;
  message: string;
} => {
  const state = getStreakDisplayState(progress);
  if (state === 'grace') {
    return {
      available: true,
      message: `⏳ Streak paused — log today to save your ${progress.current_streak}-day streak`
    };
  }
  return { available: false, message: '' };
};
